import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import SlidingTab from "../../../../components/SlidingTab/SlidingTab";
import Chart from "react-apexcharts";
import { getPortfolioVolume } from "../../../../../api";
import useDashboardStore from "../../../../stores/dashboard";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import {
  dateFormatter,
  formatCurrency,
  formatNumberForChart,
} from "../../../../../components/helper/helper";
import SkeletonLoader from "../../../../components/skeleton-loader";

const PortfolioVolume = () => {
  const isMobile = useMediaQuery(768);
  const filterDataStartDate = useDashboardStore((s) => s.filterData.startDate);
  const filterDataEndDate = useDashboardStore((s) => s.filterData.endDate);
  const filterUser = useDashboardStore((s) => s.filterData.filterUser);
  const filterOrg = useDashboardStore((s) => s.filterData.filterOrg);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("volume");
  const portfolioVolumeLoadedAtleastOnce = useRef(false);
  const chartHeight = isMobile ? 280 : 335;

  const [salesData, setSalesData] = useState([10, 20, 30]);
  const [chargebackData, setChargebackData] = useState([12, 5, 10]);
  const [dates, setDates] = useState(["Jan", "Feb", "Mar"]);
  const [returnsData, setReturnsData] = useState([10, 20, 30]);

  const chartOptions = {
    chart: {
      type: "line",
      height: chartHeight,
      toolbar: { show: false },
    },
    colors: ["#4C70EF", "#E03B24", "#EBB800"],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.3,
        gradientToColors: ["#4C70EF"],
        opacityFrom: 0.4,
        opacityTo: 0,
      },
    },
    grid: {
      borderColor: "#EDEDED",
    },
    xaxis: {
      categories: dates,
      labels: {
        formatter: (value) => `${value}`,
        style: {
          colors: "#888",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => `${type == "volume" ? "$" : ""}${formatNumberForChart(value)}`,
        style: {
          colors: "#888",
        },
      },
      // min: 420,
      // max: 520,
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "left",
      fontSize: "12px",
      fontFamily: "Work Sans",
      fontWeight: 400,
      labels: {
        colors: "#666",
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: (value) =>
          type === "volume" ? '$ ' + formatCurrency(value) : value,
      },
    },
  };

  const series = [
    {
      name: "Sales",
      data: salesData,
    },
    {
      name: "Chargebacks",
      data: chargebackData,
    },
    {
      name: "Refunds",
      data: returnsData,
    },
  ];

  useEffect(() => {
    setLoading(true);
    getPortfolioVolume({
      start_date: filterDataStartDate,
      end_date: filterDataEndDate,
      user_id: filterUser,
      org_id: filterOrg,
      type: type,
    })
      .then((res) => {
        if (res.status) {
          setSalesData(res.data.transactions);
          setChargebackData(res.data.chargebacks);
          setDates(res.data.dates);
          setReturnsData(res.data.returns);
          portfolioVolumeLoadedAtleastOnce.current = true;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterDataStartDate, filterDataEndDate, filterUser, filterOrg, type]);

  return (
    <div className={styles.portfolioVolume}>
      <div className={styles.portfolioVolumeHeader}>
        <div className={styles.portfolioVolumeHeaderLeft}>
          <h2 className={styles.portfolioVolumeTitle}>Portfolio Volume</h2>
          <p className={styles.portfolioVolumeDescription}>
            {dateFormatter(filterDataStartDate)} -{" "}
            {dateFormatter(filterDataEndDate)}
          </p>
        </div>
        <div className={styles.portfolioVolumeHeaderRight}>
          {portfolioVolumeLoadedAtleastOnce.current ? (
            <SlidingTab
              options={["Volume", "Count"]}
              onChange={(value) => setType(value.toLowerCase())}
            />
          ) : (
            <SkeletonLoader
              variant="rectangular"
              height={"32px"}
              width={"180px"}
              style={{
                borderRadius: "8px",
              }}
            />
          )}
        </div>
      </div>

      <div className={styles.portfolioVolumeChart}>
        {loading ? (
          <div className={styles.loaderContainer}>
            <SkeletonLoader
              variant="rectangular"
              height={"330px"}
              width={"100%"}
            />
          </div>
        ) : (
          <Chart
            options={chartOptions}
            series={series}
            type="area"
            height={chartHeight}
          />
        )}
      </div>
    </div>
  );
};

export default PortfolioVolume;
